/**
 * Phase 600 E2: Editor preset resolver.
 *
 * Mirrors cogo-starter/src/app-loader-preset.ts — fetches design-system preset
 * tokens from the Cogo platform Worker (GET /api/design-systems/:id) and caches
 * results for TTL_MS milliseconds to avoid redundant round-trips on re-renders.
 *
 * Intentional duplication: kept in-package to avoid cross-app coupling.
 */

import { getStudioAccessToken } from '@/services/auth-provider'
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'

const TTL_MS = 60_000
const cache = new Map<string, { ts: number; tokens: unknown }>()

async function getJwt(): Promise<string | null> {
  return getStudioAccessToken()
}

/**
 * Fetch preset tokens for `presetId` from the Worker.
 *
 * @param presetId - UUID of the design-system preset.
 * @returns        - The `tokens` payload from the Worker response, or null on
 *                   any failure (network error, non-2xx, unauthenticated, etc.).
 */
export async function fetchPresetTokensForEditor(presetId: string): Promise<unknown | null> {
  const jwt = await getJwt()
  if (!jwt) return null

  const hit = cache.get(presetId)
  if (hit && Date.now() - hit.ts < TTL_MS) return hit.tokens

  try {
    const res = await fetch(
      cogoPlatformApiUrl(`/api/design-systems/${encodeURIComponent(presetId)}`),
      {
        method: 'GET',
        headers: withCogoDevProxyHeaderRecord({ Authorization: `Bearer ${jwt}` }),
      },
    )
    if (!res.ok) return null
    const json = (await res.json()) as { tokens?: unknown }
    if (json.tokens === undefined) return null
    cache.set(presetId, { ts: Date.now(), tokens: json.tokens })
    return json.tokens
  } catch {
    return null
  }
}

/**
 * Evict a single preset from the cache (or clear all if no ID given).
 * Call after a Studio Save to force re-fetch on next load.
 */
export function clearEditorPresetCache(presetId?: string): void {
  if (presetId) cache.delete(presetId)
  else cache.clear()
}

// Cross-tab cache invalidation: Studio save → editor preview refresh
// When Studio posts {type:"invalidate", presetId} on "cogo-preset-cache",
// drop the matching entry so the next render fetches fresh tokens.
if (typeof BroadcastChannel !== 'undefined') {
  try {
    const ch = new BroadcastChannel('cogo-preset-cache')
    ch.onmessage = (e) => {
      const data = e.data as { type?: string; presetId?: string } | null
      if (!data) return
      if (data.type === 'invalidate' && data.presetId) {
        cache.delete(data.presetId)
      } else if (data.type === 'invalidate-all') {
        cache.clear()
      }
    }
  } catch {
    // ignore
  }
}
